import { HEIC_UNSUPPORTED_MESSAGE, isHeicFile } from './uploadLimits';

// Photos are downscaled in the browser before upload: a phone photo is often
// 4000px+ and 3–12MB, far more than an article column (or a retina screen)
// needs, and big files hit the per-image cap.
export const RESIZE_MAX_DIMENSION = 2560;
export const RESIZE_MIN_BYTES = 1.5 * 1024 * 1024;
const JPEG_QUALITY = 0.85;

// Raster formats the browser can decode and re-encode. GIF (animation) and
// SVG (vector) pass through untouched, as do animated PNG/WebP/AVIF files
// (see isAnimatedImageHeader).
const RESIZABLE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/avif']);

// Formats that can carry transparency; those keep PNG output when they do.
const ALPHA_CAPABLE_TYPES = new Set(['image/png', 'image/webp', 'image/avif']);

// Formats that may be animated. A canvas would keep only the first frame,
// so these are checked before decoding.
const ANIMATABLE_TYPES = new Set(['image/png', 'image/webp', 'image/avif']);

// The animation markers sit at the very start of the file.
const ANIMATION_SNIFF_BYTES = 64 * 1024;

const fourCC = (bytes, offset) => (
    offset + 4 <= bytes.length
        ? String.fromCharCode(bytes[offset], bytes[offset + 1], bytes[offset + 2], bytes[offset + 3])
        : ''
);

const uint32BE = (bytes, offset) => (
    ((bytes[offset] << 24) | (bytes[offset + 1] << 16) | (bytes[offset + 2] << 8) | bytes[offset + 3]) >>> 0
);

const uint32LE = (bytes, offset) => (
    (bytes[offset] | (bytes[offset + 1] << 8) | (bytes[offset + 2] << 16) | (bytes[offset + 3] << 24)) >>> 0
);

const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];

/**
 * True when the first bytes of a file show an animated image: an APNG
 * (`acTL` chunk before the first `IDAT`), an animated WebP (VP8X animation
 * flag, or an `ANIM`/`ANMF` chunk) or an AVIF image sequence (`avis` brand).
 */
export const isAnimatedImageHeader = (bytes) => {
    if (!bytes || bytes.length < 12) {
        return false;
    }

    if (PNG_SIGNATURE.every((value, index) => bytes[index] === value)) {
        // Chunks: 4-byte length, 4-byte type, data, 4-byte CRC.
        for (let offset = 8; offset + 8 <= bytes.length;) {
            const type = fourCC(bytes, offset + 4);
            if (type === 'acTL') {
                return true;
            }
            if (type === 'IDAT' || type === 'IEND') {
                return false;
            }
            offset += 12 + uint32BE(bytes, offset);
        }
        return false;
    }

    if (fourCC(bytes, 0) === 'RIFF' && fourCC(bytes, 8) === 'WEBP') {
        // VP8X flags byte: bit 1 marks an animation.
        if (fourCC(bytes, 12) === 'VP8X' && bytes.length > 20 && (bytes[20] & 0x02)) {
            return true;
        }
        // Chunks: fourCC, 4-byte little-endian size, data padded to even.
        for (let offset = 12; offset + 8 <= bytes.length;) {
            const type = fourCC(bytes, offset);
            if (type === 'ANIM' || type === 'ANMF') {
                return true;
            }
            const size = uint32LE(bytes, offset + 4);
            offset += 8 + size + (size % 2);
        }
        return false;
    }

    if (fourCC(bytes, 4) === 'ftyp') {
        // ftyp box: size, 'ftyp', major brand, minor version, compatible brands.
        const boxEnd = Math.min(uint32BE(bytes, 0), bytes.length);
        if (fourCC(bytes, 8) === 'avis') {
            return true;
        }
        for (let offset = 16; offset + 4 <= boxEnd; offset += 4) {
            if (fourCC(bytes, offset) === 'avis') {
                return true;
            }
        }
    }
    return false;
};

const readBytes = (blob) => {
    if (typeof blob.arrayBuffer === 'function') {
        return blob.arrayBuffer();
    }
    return new Promise((resolve, reject) => {
        const reader = new window.FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error);
        reader.readAsArrayBuffer(blob);
    });
};

const isAnimatedImageFile = async (file) => {
    if (!ANIMATABLE_TYPES.has(normalizedType(file))) {
        return false;
    }
    const buffer = await readBytes(file.slice(0, ANIMATION_SNIFF_BYTES));
    return isAnimatedImageHeader(new Uint8Array(buffer));
};

const normalizedType = (file) => (typeof file?.type === 'string' ? file.type.toLowerCase() : '');

export const isResizableImage = (file) => RESIZABLE_TYPES.has(normalizedType(file));

// Needs a real decoder and canvas encoder. jsdom (tests) and very old
// browsers lack HTMLImageElement.decode / object URLs, and there the canvas
// pipeline would throw or never call back — so uploads pass through as-is.
export const canReencodeImages = () => (
    typeof window !== 'undefined'
    && typeof document !== 'undefined'
    && typeof window.HTMLImageElement?.prototype?.decode === 'function'
    && typeof window.HTMLCanvasElement?.prototype?.toBlob === 'function'
    && typeof window.URL?.createObjectURL === 'function'
);

// Target size: longest side capped at RESIZE_MAX_DIMENSION, aspect kept.
export const fitWithin = (width, height, maxDimension = RESIZE_MAX_DIMENSION) => {
    const longest = Math.max(width, height);
    if (!longest || longest <= maxDimension) {
        return { width, height };
    }
    const scale = maxDimension / longest;
    return {
        width: Math.max(1, Math.round(width * scale)),
        height: Math.max(1, Math.round(height * scale)),
    };
};

// Decoding through <img> applies the EXIF orientation (all current
// browsers), so rotated phone photos stay upright once the metadata is gone.
const decodeImage = async (objectUrl) => {
    const image = new window.Image();
    image.decoding = 'async';
    image.src = objectUrl;
    await image.decode();
    return image;
};

const hasTransparency = (context, width, height) => {
    const { data } = context.getImageData(0, 0, width, height);
    for (let index = 3; index < data.length; index += 4) {
        if (data[index] < 255) {
            return true;
        }
    }
    return false;
};

const canvasToBlob = (canvas, type, quality) => new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), type, quality);
});

const renamedFor = (name, type) => {
    const base = (typeof name === 'string' && name ? name : 'photo').replace(/\.[^./\\]+$/, '');
    return `${base}${type === 'image/png' ? '.png' : '.jpg'}`;
};

/**
 * Re-encodes a raster photo whose longest side exceeds 2560px or whose size
 * exceeds 1.5MB: downscaled to at most 2560px, as JPEG (q≈0.85) — or PNG
 * when the image actually uses transparency. Re-encoding also drops EXIF
 * metadata such as GPS position. Resolves with the original file when no
 * change is needed, when the image is animated, when re-encoding fails or is
 * unsupported (jsdom), or when the result would not be smaller.
 */
export const downscaleImageFile = async (file) => {
    if (!isResizableImage(file) || !canReencodeImages()) {
        return file;
    }

    let objectUrl = null;
    try {
        if (await isAnimatedImageFile(file)) {
            return file;
        }
        objectUrl = window.URL.createObjectURL(file);
        const image = await decodeImage(objectUrl);
        const sourceWidth = image.naturalWidth;
        const sourceHeight = image.naturalHeight;
        if (!sourceWidth || !sourceHeight) {
            return file;
        }

        const tooLarge = Math.max(sourceWidth, sourceHeight) > RESIZE_MAX_DIMENSION;
        if (!tooLarge && file.size <= RESIZE_MIN_BYTES) {
            return file;
        }

        const { width, height } = fitWithin(sourceWidth, sourceHeight);
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const context = canvas.getContext('2d');
        if (!context) {
            return file;
        }
        context.imageSmoothingEnabled = true;
        context.imageSmoothingQuality = 'high';
        context.drawImage(image, 0, 0, width, height);

        const keepAlpha = ALPHA_CAPABLE_TYPES.has(normalizedType(file))
            && hasTransparency(context, width, height);
        const outputType = keepAlpha ? 'image/png' : 'image/jpeg';
        const blob = await canvasToBlob(canvas, outputType, keepAlpha ? undefined : JPEG_QUALITY);

        // Release the bitmap memory promptly; large photos add up in a batch.
        canvas.width = 0;
        canvas.height = 0;

        if (!blob || blob.size >= file.size) {
            return file;
        }
        return new File([blob], renamedFor(file.name, outputType), {
            type: blob.type || outputType,
            lastModified: file.lastModified,
        });
    } catch {
        return file;
    } finally {
        // Kept until after drawImage: the browser may re-read the blob.
        if (objectUrl) {
            window.URL.revokeObjectURL(objectUrl);
        }
    }
};

/**
 * Runs before every media upload (editor body, posters and thumbnails):
 * refuses HEIC photos with a readable message and downscales large photos.
 * Video and audio pass through unchanged.
 */
export const prepareMediaForUpload = async (file) => {
    if (isHeicFile(file)) {
        throw new Error(HEIC_UNSUPPORTED_MESSAGE);
    }
    return downscaleImageFile(file);
};
